import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, TextField, Button, Alert, IconButton, Slider, Tabs, Tab } from '@mui/material';
import { Settings, Save, Plus, X, FolderOpen, Play, Square, Volume2, Music, Link, Shield, Camera, Gamepad2, Monitor } from 'lucide-react';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import StorageBrowserModal from './StorageBrowserModal';

const PATREON_RE = /^https:\/\/www\.patreon\.com\/[A-Za-z0-9_-]+\/join\/?$/;
const ITCH_RE = /^https:\/\/[A-Za-z0-9_-]+\.itch\.io\/[A-Za-z0-9_-]+\/?$/;
const BUNDLE_ID_RE = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)+$/;

const SOUND_FIELDS = [
  { key: 'buttonPress',      label: 'Button Press' },
  { key: 'buttonRelease',    label: 'Button Release' },
  { key: 'chatOutgoing',     label: 'Outgoing Chat Message' },
  { key: 'chatIncoming',     label: 'Incoming Chat Message' },
  { key: 'notification',     label: 'Notification' },
  { key: 'relationPositive', label: 'Relationship Positive' },
  { key: 'relationNegative', label: 'Relationship Negative' },
  { key: 'newChapter',       label: 'New Chapter Screen' },
  { key: 'results',          label: 'Results Screen' }
];

const EMPTY_SOUNDS = () => ({
  masterVolume: 100,
  ...Object.fromEntries(SOUND_FIELDS.map(f => [f.key, { name: '', url: '' }]))
});

export default function ConfigPanel({ getToken }) {
  const { activeTeamId, activeTeam, refreshTeams } = useTeam();
  const [patreonUrl, setPatreonUrl] = useState('');
  const [itchUrl, setItchUrl] = useState('');
  const [patreonTiers, setPatreonTiers] = useState([]);
  const [newTier, setNewTier] = useState('');
  const [sounds, setSounds] = useState(EMPTY_SOUNDS());
  const [musicFolderPath, setMusicFolderPath] = useState('');
  const [authEndpoint, setAuthEndpoint] = useState('');
  const [promoEndpoint, setPromoEndpoint] = useState('');
  const [promoInfoEndpoint, setPromoInfoEndpoint] = useState('');
  const [fullGalleryTier, setFullGalleryTier] = useState('');
  const [fullStoryLogTier, setFullStoryLogTier] = useState('');
  const [instaAppName, setInstaAppName] = useState('');
  const [instaPrivateTier, setInstaPrivateTier] = useState('');
  const [instaStoriesMin, setInstaStoriesMin] = useState(2);
  const [instaStoriesMax, setInstaStoriesMax] = useState(3);

  // Game & Platforms
  const [gameTitle, setGameTitle] = useState('');
  const [appIcon, setAppIcon] = useState({ name: '', url: '' });
  const [winExeName, setWinExeName] = useState('');
  const [androidBundleId, setAndroidBundleId] = useState('');
  const [appDescription, setAppDescription] = useState('');
  const [megaUploadPath, setMegaUploadPath] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Storage browser for picking audio files or music folder
  const [storageBrowser, setStorageBrowser] = useState(null); // { soundKey: string } or { musicFolder: true }

  // Audio preview playback
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const gc = activeTeam?.gameConfig || {};
    setPatreonUrl(gc.patreonUrl || '');
    setItchUrl(gc.itchUrl || '');
    setPatreonTiers(gc.patreonTiers || []);
    setMusicFolderPath(gc.musicFolderPath || '');
    setAuthEndpoint(gc.authEndpoint || '');
    setPromoEndpoint(gc.promoEndpoint || '');
    setPromoInfoEndpoint(gc.promoInfoEndpoint || '');
    setFullGalleryTier(gc.fullGalleryTier || '');
    setFullStoryLogTier(gc.fullStoryLogTier || '');
    setInstaAppName(gc.instaAppName || '');
    setInstaPrivateTier(gc.instaPrivateTier || '');
    setInstaStoriesMin(gc.instaStoriesMin ?? 2);
    setInstaStoriesMax(gc.instaStoriesMax ?? 3);
    setGameTitle(gc.gameTitle || '');
    setAppIcon(gc.appIcon && gc.appIcon.url ? { name: gc.appIcon.name || '', url: gc.appIcon.url } : { name: '', url: '' });
    const pl = gc.platforms || {};
    setWinExeName(pl.winExeName || '');
    setAndroidBundleId(pl.androidBundleId || '');
    setAppDescription(pl.appDescription || '');
    setMegaUploadPath(pl.mega?.uploadPath || '');
    const saved = gc.sounds || {};
    setSounds({
      masterVolume: saved.masterVolume ?? 100,
      ...Object.fromEntries(SOUND_FIELDS.map(f => [
        f.key,
        { name: saved[f.key]?.name || '', url: saved[f.key]?.url || '' }
      ]))
    });
  }, [activeTeam]);

  // Cleanup audio on unmount
  useEffect(() => () => stopAudio(), []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPlayingKey(null);
  }, []);

  const toggleAudio = useCallback((key, url) => {
    if (playingKey === key) { stopAudio(); return; }
    stopAudio();
    if (url) {
      const a = new Audio(url);
      a.volume = (sounds.masterVolume ?? 100) / 100;
      a.addEventListener('ended', () => setPlayingKey(null));
      a.play().catch(() => {});
      audioRef.current = a;
      setPlayingKey(key);
    }
  }, [playingKey, stopAudio, sounds.masterVolume]);

  const patreonError = patreonUrl && !PATREON_RE.test(patreonUrl)
    ? 'Format: https://www.patreon.com/somepage/join'
    : '';
  const itchError = itchUrl && !ITCH_RE.test(itchUrl)
    ? 'Format: https://somedev.itch.io/some-game'
    : '';
  const bundleIdError = androidBundleId && !BUNDLE_ID_RE.test(androidBundleId)
    ? 'Format: com.studio.mygame (lowercase, min 2 segments)'
    : '';

  const hasErrors = !!(patreonError || itchError || bundleIdError);

  const save = useCallback(async () => {
    if (hasErrors || !activeTeamId) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/teams/${encodeURIComponent(activeTeamId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gameConfig: {
            patreonUrl: patreonUrl.trim(),
            itchUrl: itchUrl.trim(),
            patreonTiers: patreonTiers.filter(Boolean),
            sounds,
            musicFolderPath: musicFolderPath.trim(),
            authEndpoint: authEndpoint.trim(),
            promoEndpoint: promoEndpoint.trim(),
            promoInfoEndpoint: promoInfoEndpoint.trim(),
            fullGalleryTier,
            fullStoryLogTier,
            instaAppName: instaAppName.trim(),
            instaPrivateTier: instaPrivateTier.trim(),
            instaStoriesMin,
            instaStoriesMax,
            gameTitle: gameTitle.trim(),
            appIcon,
            platforms: {
              winExeName: winExeName.trim(),
              androidBundleId: androidBundleId.trim(),
              mega: { uploadPath: megaUploadPath.trim() },
              appDescription: appDescription.trim()
            }
          }
        })
      });
      if (!res.ok) throw new Error('Failed to save config');
      setSuccess('Config saved');
      refreshTeams();
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [activeTeamId, patreonUrl, itchUrl, patreonTiers, sounds, musicFolderPath, authEndpoint, promoEndpoint, promoInfoEndpoint, fullGalleryTier, fullStoryLogTier, instaAppName, instaPrivateTier, instaStoriesMin, instaStoriesMax, gameTitle, appIcon, winExeName, androidBundleId, appDescription, megaUploadPath, hasErrors, getToken, refreshTeams]);

  const updateSound = useCallback((key, name, url) => {
    setSounds(prev => ({ ...prev, [key]: { name, url } }));
  }, []);

  const clearSound = useCallback((key) => {
    stopAudio();
    setSounds(prev => ({ ...prev, [key]: { name: '', url: '' } }));
  }, [stopAudio]);

  const hasGame = !!(activeTeam?.gameTemplate);
  const [tab, setTab] = useState(0);

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: '#a78bfa', display: 'flex', alignItems: 'center' }}>
          <Settings size={22} strokeWidth={2} />
        </Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Config
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 36,
          mb: 2,
          '& .MuiTab-root': {
            minHeight: 36,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.82rem',
            px: 1.5,
            minWidth: 'auto',
          },
          '& .MuiTabs-indicator': { backgroundColor: '#a78bfa' },
          '& .Mui-selected': { color: '#a78bfa !important' },
        }}
      >
        {hasGame && <Tab value={0} icon={<Gamepad2 size={15} />} iconPosition="start" label="Game" />}
        {hasGame && <Tab value={1} icon={<Link size={15} />} iconPosition="start" label="General" />}
        {hasGame && <Tab value={2} icon={<Volume2 size={15} />} iconPosition="start" label="Sounds" />}
        {hasGame && <Tab value={3} icon={<Music size={15} />} iconPosition="start" label="Music" />}
        {hasGame && <Tab value={4} icon={<Monitor size={15} />} iconPosition="start" label="Platforms" />}
      </Tabs>

      <Box display="flex" flexDirection="column" gap={2} maxWidth="32rem">
        {/* ── Tab 0: Game ── */}
        {tab === 0 && (
          <>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                <Gamepad2 size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                Game Identity
              </Typography>
              <TextField
                label="Game Title"
                fullWidth
                size="small"
                value={gameTitle}
                onChange={(e) => setGameTitle(e.target.value)}
                placeholder="My Awesome Game"
                helperText="Used as window title, app name, and build artifact name"
              />
            </Box>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                App Icon
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Square image used as the app icon for Windows (.ico) and Android builds. Recommended: 512x512 PNG or ICO.
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {appIcon.url && (
                  <Box
                    component="img"
                    src={appIcon.url}
                    alt="App icon"
                    sx={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                )}
                <Typography
                  sx={{ fontSize: '0.8rem', fontWeight: 500, color: appIcon.name ? '#fafafa' : 'text.disabled', flex: 1 }}
                  noWrap
                >
                  {appIcon.name || 'No icon selected'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FolderOpen size={14} />}
                  onClick={() => setStorageBrowser({ appIcon: true })}
                  sx={{ flexShrink: 0 }}
                >
                  Browse
                </Button>
                {appIcon.name && (
                  <Button size="small" color="error" onClick={() => setAppIcon({ name: '', url: '' })} sx={{ flexShrink: 0 }}>
                    Clear
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}

        {/* ── Tab 1: General ── */}
        {tab === 1 && (
          <>
            {/* Links */}
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Links
              </Typography>
              <TextField
                label="Patreon URL"
                fullWidth
                size="small"
                value={patreonUrl}
                onChange={(e) => setPatreonUrl(e.target.value)}
                error={!!patreonError}
                helperText={patreonError || 'https://www.patreon.com/somepage/join'}
                placeholder="https://www.patreon.com/somepage/join"
              />
              <TextField
                label="Itch URL"
                fullWidth
                size="small"
                value={itchUrl}
                onChange={(e) => setItchUrl(e.target.value)}
                error={!!itchError}
                helperText={itchError || 'https://somedev.itch.io/some-game'}
                placeholder="https://somedev.itch.io/some-game"
              />
            </Box>

            {/* Patreon Tiers */}
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                Patreon Tiers
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75}>
                {patreonTiers.map((tier, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      fullWidth
                      value={tier}
                      onChange={(e) => {
                        const nt = [...patreonTiers];
                        nt[i] = e.target.value;
                        setPatreonTiers(nt);
                      }}
                      placeholder="Tier name"
                    />
                    <IconButton
                      size="small"
                      onClick={() => setPatreonTiers(patreonTiers.filter((_, j) => j !== i))}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                    >
                      <X size={16} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <TextField
                  size="small"
                  fullWidth
                  value={newTier}
                  onChange={(e) => setNewTier(e.target.value)}
                  placeholder="New tier name..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTier.trim()) {
                      setPatreonTiers([...patreonTiers, newTier.trim()]);
                      setNewTier('');
                    }
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!newTier.trim()}
                  onClick={() => { setPatreonTiers([...patreonTiers, newTier.trim()]); setNewTier(''); }}
                  startIcon={<Plus size={14} />}
                  sx={{ flexShrink: 0 }}
                >
                  Add
                </Button>
              </Box>
            </Box>
            {/* Endpoints */}
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Endpoints
              </Typography>
              <TextField
                label="Auth Endpoint"
                fullWidth
                size="small"
                value={authEndpoint}
                onChange={(e) => setAuthEndpoint(e.target.value)}
                placeholder="https://mypatreonserver/api/session/check"
                helperText="Session check endpoint for Patreon auth"
              />
              <TextField
                label="Promo Endpoint"
                fullWidth
                size="small"
                value={promoEndpoint}
                onChange={(e) => setPromoEndpoint(e.target.value)}
                placeholder="https://mypatreonserver/api/promo/activate"
                helperText="Promo code activation endpoint"
              />
              <TextField
                label="Promo Info Endpoint"
                fullWidth
                size="small"
                value={promoInfoEndpoint}
                onChange={(e) => setPromoInfoEndpoint(e.target.value)}
                placeholder="https://mypatreonserver/api/promo/info"
                helperText="Promo code info endpoint (GET with /{code})"
              />
            </Box>

            {/* Tier Requirements */}
            {patreonTiers.length > 0 && (
              <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                  <Shield size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                  Tier Requirements
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Full Gallery Tier</InputLabel>
                  <Select
                    value={fullGalleryTier}
                    onChange={(e) => setFullGalleryTier(e.target.value)}
                    label="Full Gallery Tier"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {patreonTiers.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Full Story Log Tier</InputLabel>
                  <Select
                    value={fullStoryLogTier}
                    onChange={(e) => setFullStoryLogTier(e.target.value)}
                    label="Full Story Log Tier"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {patreonTiers.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Social Apps */}
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                <Camera size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                Social Apps
              </Typography>
              <TextField
                label="In-game Instagram name"
                size="small"
                fullWidth
                placeholder="e.g. Sextagram"
                value={instaAppName}
                onChange={(e) => setInstaAppName(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Private Posts Tier</InputLabel>
                <Select
                  value={instaPrivateTier}
                  onChange={(e) => setInstaPrivateTier(e.target.value)}
                  label="Private Posts Tier"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {patreonTiers.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box display="flex" gap={1}>
                <TextField
                  label="Stories/day min"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={instaStoriesMin}
                  onChange={(e) => setInstaStoriesMin(Math.max(0, parseInt(e.target.value) || 0))}
                />
                <TextField
                  label="Stories/day max"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={instaStoriesMax}
                  onChange={(e) => setInstaStoriesMax(Math.max(0, parseInt(e.target.value) || 0))}
                />
              </Box>
            </Box>
          </>
        )}

        {/* ── Tab 2: Sounds ── */}
        {tab === 2 && (
          <>
            {/* Master Volume */}
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Master Volume — {sounds.masterVolume}%
              </Typography>
              <Slider
                size="small"
                value={sounds.masterVolume}
                min={0}
                max={100}
                onChange={(_, v) => setSounds(prev => ({ ...prev, masterVolume: v }))}
                sx={{
                  color: '#fbbf24',
                  '& .MuiSlider-thumb': { width: 14, height: 14 }
                }}
              />
            </Box>

            {/* Sound rows */}
            <Box display="flex" flexDirection="column" gap={1.5}>
              {SOUND_FIELDS.map(({ key, label }) => {
                const s = sounds[key] || {};
                const isPlaying = playingKey === key;
                return (
                  <Box key={key} sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      {label}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {s.url && (
                        <IconButton
                          size="small"
                          onClick={() => toggleAudio(key, s.url)}
                          sx={{ p: 0.5, color: isPlaying ? '#fbbf24' : 'text.disabled', '&:hover': { color: '#fbbf24' } }}
                        >
                          {isPlaying
                            ? <Square size={14} fill="#fbbf24" />
                            : <Play size={14} fill="currentColor" />
                          }
                        </IconButton>
                      )}
                      <Typography
                        sx={{ fontSize: '0.8rem', fontWeight: 500, color: s.name ? '#fafafa' : 'text.disabled', flex: 1 }}
                        noWrap
                      >
                        {s.name || 'No audio'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen size={14} />}
                        onClick={() => setStorageBrowser({ soundKey: key })}
                        sx={{ flexShrink: 0 }}
                      >
                        Browse
                      </Button>
                      {s.name && (
                        <Button size="small" color="error" onClick={() => clearSound(key)} sx={{ flexShrink: 0 }}>
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* ── Tab 3: Music ── */}
        {tab === 3 && (
          <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Music Folder
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Select a Storage folder containing audio files for the in-game Sexify music player.
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{ fontSize: '0.8rem', fontWeight: 500, color: musicFolderPath ? '#fafafa' : 'text.disabled', flex: 1 }}
                noWrap
              >
                {musicFolderPath || 'No folder selected'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FolderOpen size={14} />}
                onClick={() => setStorageBrowser({ musicFolder: true })}
                sx={{ flexShrink: 0 }}
              >
                Browse
              </Button>
              {musicFolderPath && (
                <Button size="small" color="error" onClick={() => setMusicFolderPath('')} sx={{ flexShrink: 0 }}>
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* ── Tab 4: Platforms ── */}
        {tab === 4 && (
          <>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Windows
              </Typography>
              <TextField
                label="EXE Name"
                fullWidth
                size="small"
                value={winExeName}
                onChange={(e) => setWinExeName(e.target.value)}
                placeholder="MyGame"
                helperText="Name for the Windows executable (without .exe)"
              />
            </Box>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Android
              </Typography>
              <TextField
                label="Bundle ID"
                fullWidth
                size="small"
                value={androidBundleId}
                onChange={(e) => setAndroidBundleId(e.target.value)}
                error={!!bundleIdError}
                helperText={bundleIdError || 'e.g. com.studio.mygame'}
                placeholder="com.studio.mygame"
              />
            </Box>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Description
              </Typography>
              <TextField
                label="App Description"
                fullWidth
                size="small"
                multiline
                minRows={2}
                maxRows={4}
                value={appDescription}
                onChange={(e) => setAppDescription(e.target.value)}
                placeholder="A short description of your game..."
              />
            </Box>
            <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                Mega
              </Typography>
              <TextField
                label="Upload Path"
                fullWidth
                size="small"
                value={megaUploadPath}
                onChange={(e) => setMegaUploadPath(e.target.value)}
                placeholder="builds/my-game"
                helperText="Path in any format."
              />
            </Box>
          </>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save size={16} strokeWidth={2} />}
            onClick={save}
            disabled={saving || hasErrors}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Storage browser for audio selection or music folder */}
      <StorageBrowserModal
        open={!!storageBrowser && !storageBrowser.musicFolder && !storageBrowser.appIcon}
        onClose={() => setStorageBrowser(null)}
        fileFilter="audio"
        teamId={activeTeamId}
        getToken={getToken}
        title="Select Sound"
        onSelect={(item) => {
          if (storageBrowser?.soundKey) {
            updateSound(storageBrowser.soundKey, item.name, item.publicUrl || '');
          }
          setStorageBrowser(null);
        }}
      />
      <StorageBrowserModal
        open={!!storageBrowser?.musicFolder}
        onClose={() => setStorageBrowser(null)}
        folderSelect
        teamId={activeTeamId}
        getToken={getToken}
        title="Select Music Folder"
        onSelect={(item) => {
          setMusicFolderPath(item.fullPath || '');
          setStorageBrowser(null);
        }}
      />
      <StorageBrowserModal
        open={!!storageBrowser?.appIcon}
        onClose={() => setStorageBrowser(null)}
        fileFilter="image"
        teamId={activeTeamId}
        getToken={getToken}
        title="Select App Icon"
        onSelect={(item) => {
          setAppIcon({ name: item.name, url: item.publicUrl || '' });
          setStorageBrowser(null);
        }}
      />
    </>
  );
}
